import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { MouseEventHandler, ReactElement, useEffect, useState } from "react";

import { SVG_MUI } from "@ryde/assets";
import {
  NUMBER_COMPARISON_OPERATORS,
  NUMBER_COMPARISON_SEPARATOR,
  ValueType,
} from "@ryde/shared";

import {
  NumberFieldWrapper,
  NumberSelectWrapper,
  TextFieldInputStyled,
  TextFieldLabel,
} from "../number.styled";

interface INumberComparisonFilterFieldProps {
  fieldId: string;
  label: string;
  /** The stored filter value: a single `"op:value"` encoded string (array-wrapped), or empty. */
  value: ValueType | null | undefined;
  /** Called with the encoded `"op:value"` string, or `undefined` when the filter is incomplete — clear it. */
  onApply: (encodedValue: string | undefined) => void;
  onRemove: () => void;
}

/**
 * The "pick an operator, type a number" filter chip: label, an operator select, a number field and an X
 * to remove. Owns the field state and the `"op:value"` encoding; the caller owns where the value is stored
 * and how it is labelled, so the grid (custom field id) and the reports bar (template key) share one field.
 */
export const NumberComparisonFilterField = ({
  fieldId,
  label,
  value,
  onApply,
  onRemove,
}: INumberComparisonFilterFieldProps): ReactElement => {
  const [operator, setOperator] = useState<string>("");
  const [numberValue, setNumberValue] = useState<string>("");

  useEffect(() => {
    // Only null/undefined means an external clear (clear-all writes null). An EMPTY ARRAY is the store
    // round-trip of this field's own incomplete edit (onApply(undefined) → caller stores []) — resetting
    // on it would erase the operator the moment the number is deleted, dead-ending the field because the
    // number input is disabled without an operator.
    if (value === null || value === undefined) {
      setOperator("");
      setNumberValue("");
      return;
    }

    const stored = Array.isArray(value) ? value[0] : value;

    if (typeof stored !== "string" || !stored) {
      return;
    }

    const [storedOperator, storedNumber] = stored.split(
      NUMBER_COMPARISON_SEPARATOR,
    );
    setOperator(storedOperator ?? "");
    setNumberValue(storedNumber ?? "");
  }, [value]);

  const applyValue = (nextOperator: string, nextNumber: string): void => {
    onApply(
      nextOperator && nextNumber
        ? `${nextOperator}${NUMBER_COMPARISON_SEPARATOR}${nextNumber}`
        : undefined,
    );
  };

  const handleOperatorChange = (event: SelectChangeEvent<string>): void => {
    const newOperator = event.target.value;
    setOperator(newOperator);

    if (numberValue) {
      applyValue(newOperator, numberValue);
    }
  };

  const handleNumberValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const newValue = event.target.value.replace(/[^0-9.]/g, "");
    setNumberValue(newValue);

    if (operator) {
      applyValue(operator, newValue);
    }
  };

  const removeFilter: MouseEventHandler = e => {
    e.stopPropagation();
    onRemove();
  };

  return (
    <NumberFieldWrapper>
      <TextFieldLabel htmlFor={fieldId}>{label}</TextFieldLabel>

      <NumberSelectWrapper>
        <Select value={operator} onChange={handleOperatorChange}>
          {NUMBER_COMPARISON_OPERATORS.map(op => (
            <MenuItem key={op.value} value={op.value}>
              {op.label}
            </MenuItem>
          ))}
        </Select>
      </NumberSelectWrapper>

      <TextFieldInputStyled
        name={fieldId}
        placeholder="--"
        value={numberValue}
        onChange={handleNumberValueChange}
        disabled={!operator}
      />

      <SVG_MUI.CircleXMark
        className="circle-x-mark"
        onClick={e => removeFilter(e)}
      />
    </NumberFieldWrapper>
  );
};
